import * as React from "react";
import { Button, Modal } from "antd";
import type { ButtonProps } from "antd";
import type { ModalProps } from "antd";
import InfoBanner from "./InfoBanner";

type AppModalInfoBanner = {
    description: React.ReactNode;
    showIcon?: boolean;
    title?: React.ReactNode;
};

type AppModalActionClick = (
    event: React.MouseEvent<HTMLElement>,
) => void | Promise<void>;

type AppModalProps = ModalProps & {
    cancelButtonProps?: ButtonProps;
    cancelButtonTitle?: React.ReactNode;
    "data-id-name"?: string;
    infoBanner?: AppModalInfoBanner;
    infoBannerPlacement?: "bottom" | "top";
    onCancelClick?: AppModalActionClick;
    onSubmitClick?: AppModalActionClick;
    submitButtonProps?: ButtonProps;
    submitButtonTitle?: React.ReactNode;
    title?: React.ReactNode;
};

const getTextContent = (node: React.ReactNode): string => {
    if (typeof node === "string" || typeof node === "number") {
        return node.toString();
    }

    if (Array.isArray(node)) {
        return node.map(getTextContent).join(" ");
    }

    if (React.isValidElement(node)) {
        return getTextContent(node.props.children);
    }

    return "";
};

const toDataIdPart = (value?: React.ReactNode) => {
    const text = getTextContent(value).trim();

    if (!text) {
        return undefined;
    }

    return text
        .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
};

const renderInfoBanner = (infoBanner: AppModalInfoBanner) => {
    return <InfoBanner {...infoBanner} />;
};

const renderTitle = (
    title: React.ReactNode,
    infoBanner?: AppModalInfoBanner,
) => {
    const titleContent =
        typeof title === "string" ? (
            <h2 className="app-modal__title">{title}</h2>
        ) : (
            title
        );

    if (!infoBanner) {
        return titleContent;
    }

    return (
        <div className="app-modal__title-container">
            {titleContent}
            {renderInfoBanner(infoBanner)}
        </div>
    );
};

const AppModal: React.FC<AppModalProps> = ({
    cancelButtonProps,
    cancelButtonTitle,
    children,
    className,
    infoBanner,
    infoBannerPlacement = "top",
    onCancelClick,
    onSubmitClick,
    submitButtonProps,
    submitButtonTitle,
    title,
    width = 600,
    ...modalProps
}) => {
    const modalClassName = className ? `app-modal ${className}` : "app-modal";
    const shouldRenderInfoInTitle =
        infoBannerPlacement === "top" && !!infoBanner;
    const shouldRenderInfoInBody =
        infoBannerPlacement === "bottom" && !!infoBanner;
    const shouldRenderActionFooter =
        modalProps.footer === undefined &&
        (cancelButtonTitle !== undefined || submitButtonTitle !== undefined);
    const actionDataIdBase = toDataIdPart(modalProps["data-id-name"]);
    const cancelButtonDataId = actionDataIdBase
        ? `button-${actionDataIdBase}-cancel`
        : undefined;
    const submitButtonDataId = actionDataIdBase
        ? `button-${actionDataIdBase}-submit`
        : undefined;
    const actionFooter = shouldRenderActionFooter
        ? [
              cancelButtonTitle !== undefined ? (
                  <Button
                      key="app-modal-cancel"
                      data-id={cancelButtonDataId}
                      size="large"
                      className="light"
                      onClick={(event) => {
                          void onCancelClick?.(event);
                      }}
                      {...cancelButtonProps}
                  >
                      {cancelButtonTitle}
                  </Button>
              ) : null,
              submitButtonTitle !== undefined ? (
                  <Button
                      key="app-modal-submit"
                      data-id={submitButtonDataId}
                      size="large"
                      type="primary"
                      htmlType="submit"
                      onClick={(event) => {
                          void onSubmitClick?.(event);
                      }}
                      {...submitButtonProps}
                  >
                      {submitButtonTitle}
                  </Button>
              ) : null,
          ].filter(Boolean)
        : modalProps.footer;

    return (
        <Modal
            {...modalProps}
            className={modalClassName}
            footer={actionFooter}
            title={
                title === undefined || title === null
                    ? title
                    : renderTitle(
                          title,
                          shouldRenderInfoInTitle ? infoBanner : undefined,
                      )
            }
            width={width}
        >
            {children}
            {shouldRenderInfoInBody ? renderInfoBanner(infoBanner) : null}
        </Modal>
    );
};

export default AppModal;
